import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box, Paper, Typography, Button, Alert, CircularProgress,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import MarkEmailReadIcon from '@mui/icons-material/MarkEmailRead';
import { confirmarEmail } from '../services/authApi';

// Página /confirmar-email?email=X&token=Y
// Se abre cuando el afiliado hace clic en el link del correo de confirmación.
// Al montar, hace la petición al backend y muestra el resultado.
export default function ConfirmarEmail() {
  const location = useLocation();
  const navigate = useNavigate();

  const params = new URLSearchParams(location.search);
  const email  = params.get('email')  || '';
  const token  = params.get('token')  || '';

  const [estado, setEstado] = useState('confirmando'); // confirmando | ok | error
  const [mensaje, setMensaje] = useState('');

  // Evitar doble llamada por StrictMode en desarrollo
  const yaEjecutado = useRef(false);

  useEffect(() => {
    if (yaEjecutado.current) return;
    yaEjecutado.current = true;

    if (!email || !token) {
      setEstado('error');
      setMensaje('Enlace inválido. Solicita uno nuevo desde la página de afiliación.');
      return;
    }

    (async () => {
      try {
        const resp = await confirmarEmail(email, token);
        setEstado('ok');
        setMensaje(resp?.mensaje || '¡Tu correo fue confirmado!');
      } catch (err) {
        setEstado('error');
        setMensaje(err?.data?.mensaje || 'No se pudo confirmar el correo. El enlace puede haber expirado.');
      }
    })();
  }, [email, token]);

  return (
    <Box sx={{
      minHeight: 'calc(100vh - 200px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'linear-gradient(135deg, #f0f7f2 0%, #e8f5e9 100%)',
      p: 2,
    }}>
      <Paper elevation={3} sx={{ p: 4, maxWidth: 480, width: '100%', borderRadius: 3 }}>
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <MarkEmailReadIcon sx={{ fontSize: 64, color: '#1a4d2e' }}/>
          <Typography variant="h5" sx={{ mt: 1, fontWeight: 700, color: '#1a4d2e' }}>
            Confirmación de correo
          </Typography>
          <Typography variant="body2" sx={{ color: '#666', mt: 0.5 }}>
            FENAGRO APL — Red Alianza Solidaria
          </Typography>
        </Box>

        {estado === 'confirmando' && (
          <Box sx={{ textAlign: 'center', py: 3 }}>
            <CircularProgress size={40} sx={{ color: '#1a4d2e' }}/>
            <Typography sx={{ mt: 2, color: '#555' }}>Confirmando tu correo…</Typography>
          </Box>
        )}

        {estado === 'ok' && (
          <>
            <Alert severity="success" icon={<CheckCircleOutlineIcon/>} sx={{ mb: 2 }}>
              <Typography sx={{ fontWeight: 700 }}>¡Correo confirmado!</Typography>
              <Typography variant="body2">{mensaje}</Typography>
            </Alert>
            <Typography variant="body2" sx={{ color: '#555', mb: 2 }}>
              Ya puedes continuar con los siguientes pasos de tu afiliación: pagar la inscripción,
              agendar tu sesión de orientación y subir los documentos.
            </Typography>
            <Button fullWidth variant="contained"
              onClick={() => navigate('/afiliacion')}
              sx={{ background: '#1a4d2e', '&:hover': { background: '#0e2a18' }, fontWeight: 700 }}>
              Continuar mi afiliación
            </Button>
          </>
        )}

        {estado === 'error' && (
          <>
            <Alert severity="error" icon={<ErrorOutlineIcon/>} sx={{ mb: 2 }}>
              <Typography sx={{ fontWeight: 700 }}>No se pudo confirmar</Typography>
              <Typography variant="body2">{mensaje}</Typography>
            </Alert>
            <Typography variant="body2" sx={{ color: '#555', mb: 2 }}>
              Es posible que el enlace haya expirado. Ingresa a la página de afiliación,
              consulta tu solicitud con tu número de cédula y solicita un nuevo enlace.
            </Typography>
            <Button fullWidth variant="outlined"
              onClick={() => navigate('/afiliacion')}
              sx={{ borderColor: '#1a4d2e', color: '#1a4d2e', fontWeight: 700 }}>
              Ir a Afiliación
            </Button>
          </>
        )}
      </Paper>
    </Box>
  );
}
